import Header from "@/components/Header";
import GraduatingCoinsCarousel from "@/components/GraduatingCoinsCarousel";
import CoinControls from "@/components/CoinControls";
import CoinsTable from "@/components/CoinsTable";
import { useCoins, fallbackCoins } from "@/hooks/useCoins";
import { useAccount, usePublicClient } from "wagmi";
import { Address, parseAbiItem } from "viem";
import { useEffect, useMemo, useState } from "react";

const BALANCE_OF = [parseAbiItem("function balanceOf(address account) view returns (uint256)")] as const;

const Advanced = () => {
  const { address } = useAccount();
  const publicClient = usePublicClient({ chainId: 137 });
  const { data, isLoading, isError } = useCoins(50);
  const [showOwned, setShowOwned] = useState(false);
  const [ownedAddresses, setOwnedAddresses] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<"newest" | "volume" | "marketCap">("newest");
  const sourceCoins = data?.coins || fallbackCoins;
  useEffect(() => {
    let cancelled = false;
    const checkBalances = async () => {
      if (!address || !publicClient) return setOwnedAddresses(new Set());
      const results = await Promise.all(sourceCoins.map(async (coin) => {
        if (!coin.coinAddress || !/^0x[a-fA-F0-9]{40}$/.test(coin.coinAddress)) return null;
        try {
          const balance = await publicClient.readContract({ address: coin.coinAddress as Address, abi: BALANCE_OF, functionName: "balanceOf", args: [address as Address] });
          return (balance as bigint) > 0n ? coin.coinAddress.toLowerCase() : null;
        } catch { return null; }
      }));
      if (!cancelled) setOwnedAddresses(new Set(results.filter(Boolean) as string[]));
    };
    checkBalances();
    return () => { cancelled = true; };
  }, [address, publicClient, sourceCoins]);
  const coins = useMemo(() => {
    const visible = showOwned ? sourceCoins.filter((coin) => coin.coinAddress && ownedAddresses.has(coin.coinAddress.toLowerCase())) : sourceCoins;
    return [...visible].sort((a, b) => sort === "volume" ? b.volume.localeCompare(a.volume) : sort === "marketCap" ? b.marketCap.localeCompare(a.marketCap) : 0);
  }, [sourceCoins, showOwned, ownedAddresses, sort]);
  const graduatingCoins = sourceCoins.slice(0, 6).map((coin) => ({ name: coin.name, marketCap: coin.marketCap, volume: coin.volume }));

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-16">
        <GraduatingCoinsCarousel coins={graduatingCoins} />
        <CoinControls showOwned={showOwned} onToggleOwned={() => setShowOwned((value) => !value)} sort={sort} onSortChange={() => setSort((value) => value === "newest" ? "volume" : value === "volume" ? "marketCap" : "newest")} />
        {isError && <p className="mb-4 text-sm text-muted-foreground">Live coin data is unavailable. Showing example communities.</p>}
        {isLoading ? <div className="py-12 text-center text-muted-foreground">Loading live Polygon coins…</div> : <CoinsTable coins={coins} />}
      </main>
    </div>
  );
};

export default Advanced;
